/**
 * SewerGate — a signed-in subcontractor sees the portal and nothing else.
 *
 * i-SYNERGY reads SharePoint with the person's own rights, so a sewer who wandered into the rest of
 * the app would only find denied lists — but they should never be shown the modules at all. This asks
 * /api/portal/me once per browser session and keeps a LINKED, NON-STAFF account on /sewer.
 *
 * FAILS OPEN, on purpose: if the API is not configured, is slow, or errors, everybody renders as before.
 * The data boundary is the server, never this redirect (see api/src/shared/sewerPortal.ts).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { canOpenSubcon } from "../subcontractors/subconAccess";
import { portalMe, type IPortalMe } from "./portalClient";

const KEY = "sewer-gate:";
const WAIT_MS = 2500;

const read = (who: string): IPortalMe | null => {
  try { const v = sessionStorage.getItem(KEY + who); return v ? (JSON.parse(v) as IPortalMe) : null; } catch { return null; }
};

export function SewerGate({ children, fallback }: { children: ReactNode; fallback: ReactNode }): JSX.Element {
  const { accounts } = useMsal();
  const who = (accounts[0]?.username ?? "").toLowerCase();
  const location = useLocation();
  const [me, setMe] = useState<IPortalMe | null | "open">(() => (who ? read(who) : "open"));

  // ALWAYS ask, even with a stored answer: the stored one only paints the first frame. It used to be
  // trusted for the whole browser session — and Chrome restores a session's storage with "Continue where
  // you left off" — so an answer from before a sign-in was linked ("not a sewer") kept that person on the
  // full Home for as long as the tab lived (2026-10-29, a linked sewer on a laptop saw every module).
  useEffect(() => {
    if (!who) return;
    let live = true;
    const timer = setTimeout(() => { if (live) setMe((cur) => cur ?? "open"); }, WAIT_MS);
    portalMe()
      .then((r) => {
        if (!live) return;
        // Only a positive answer is kept: a "not linked" one is exactly what goes stale.
        try {
          if (r.mapped || r.staff) sessionStorage.setItem(KEY + who, JSON.stringify(r)); else sessionStorage.removeItem(KEY + who);
        } catch { /* private mode */ }
        setMe(r);
      })
      .catch(() => { if (live) setMe((cur) => cur ?? "open"); })
      .finally(() => clearTimeout(timer));
    return () => { live = false; clearTimeout(timer); };
  }, [who]);

  if (me === null) return <>{fallback}</>;
  // Authorised personnel are never kept on the sewer app, even if their own account is also linked to a
  // subcontractor key (a test link): the API says staff, or the sign-in is on the Subcontractors access list.
  const personnel = canOpenSubcon(who);
  const onSewerPath = location.pathname === "/sewer" || location.pathname.startsWith("/sewer/");
  if (me !== "open" && me.mapped && !me.staff && !personnel && !onSewerPath) return <Navigate to="/sewer" replace />;
  return <>{children}</>;
}
